import React, { useState, useEffect } from 'react';
import { AiOutlineFolderAdd, AiOutlineFileAdd, AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import { getFolders, deleteFolder, deleteFile, getFiles } from '../../../api/api';
import AddFolderModal from './AddFolder';
import AddFileModal from './AddFiles';
import Breadcrumb from '../../Breadcrumb'; // Breadcrumb component for navigation
import { Loader } from '@mantine/core';
import ConfrimDelete from '../../ConfirmationModal'
import FileRendererWithDialog from './FileRenderer';
const FolderFileManager = ({ projectName, project_id }) => {
  const [existingFolders, setExistingFolders] = useState([]); // Default to empty array
  const [allFiles, setAllFiles] = useState([]); // Default to empty array
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isAddFileModalOpen, setIsAddFileModalOpen] = useState(false);
  const [path, setPath] = useState(projectName); // Set initial path to projectName
  const [pageUpdate, setPageUpdate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // State to hold selected file for rendering
  const [loading, setLoading] = useState(true);  // Track loading state
  const [open, setOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // Holds the item being deleted
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [itemToEdit, setItemtoEdit] = useState(null);

  // Fetch both folders and files for a given path
  const fetchFoldersAndFiles = async (currentPath) => {
    setLoading(true); // Set loading to true when the request starts
    try {
      const folderResponse = await getFolders({ projectId: project_id, folder_name: currentPath });
      // console.log(folderResponse.data.data);
      setExistingFolders(folderResponse.data.data || []); // Default to empty array if no data
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setLoading(false);  // Set loading to false after request finishes
    }
  };

  const fetchFiles = async (currentPath) => {
    // console.log(currentPath)
    setLoading(true); // Set loading to true when the request starts
    try {
      const fileResponse = await getFiles({ projectId: project_id, folderPath: currentPath });
      setAllFiles(fileResponse.data.files || []); // Default to empty array if no data
      // console.log(fileResponse);
    } catch (error) {
      setAllFiles(null);
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);  // Set loading to false after request finishes
    }
  };
  const handleEdit = (item, type) => {
    setItemtoEdit(item); // Store the item being edited
    if (type === 'file') {
      setIsAddFileModalOpen(true); // Open Add File modal
    } else if (type === 'folder') {
      setIsFolderModalOpen(true); // Open Add Folder modal
    }
  };

  // Trigger page update (force re-render)
  const update = () => {
    setPageUpdate(!pageUpdate);
  };

  // Handle folder toggle to show or hide its content
  const handleFolderToggle = async (index, folderName) => {
    const updatedFolders = [...existingFolders];
    updatedFolders[index].isOpen = !updatedFolders[index].isOpen;
    setExistingFolders(updatedFolders);

    if (updatedFolders[index].isOpen) {
      const newPath = `${path}/${folderName}`;
      setPath(newPath); // Update the path directly in state

      try {
        const response = await getFolders({ projectId: project_id, folder_name: newPath });
        updatedFolders[index].files = response.data.data || [];
        setExistingFolders(updatedFolders);
        update()
      } catch (error) {
        console.error('Error fetching folder content:', error);
      }
    }
  };

  // Handle file click, open file if it's a file type
  const handleFileClick = (file) => {
    if (file.type === 'folder') {
      const newPath = `${path}/${file.name}`;
      setPath(newPath); // Update the path directly in state
      fetchFoldersAndFiles(newPath); // Re-fetch folders and files
      console.log('Path:', path)
    } else {

      setOpen(true)
      setSelectedFile(file); // Set selected file to render in FileRenderer
    }
  };
  const openDeleteConfirmation = (item, type) => {
    setItemToDelete({ ...item, type }); // Include `type` ('file' or 'folder')
    setConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'file') {
        const data = {

          fileId: itemToDelete._id
        };
        const response = await deleteFile(data);
        console.log(response);
        update()
      } else if (itemToDelete.type === 'folder') {
        console.log(itemToDelete);
        const response = await deleteFolder({
          projectId: project_id,
          folder_name: `${path}/${itemToDelete.name}`,
        });
        update()
      }
      setConfirmModalOpen(false);
      fetchFoldersAndFiles(path); // Refresh data after deletion
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  // Handle breadcrumb click to navigate to a different path
  const handleBreadcrumbClick = (breadcrumbPath) => {
    setPath(breadcrumbPath);
    fetchFoldersAndFiles(breadcrumbPath);
  };
    const closeModal = () => {
    setOpen(false);
    setSelectedFile(null);
  };

  // Use effect to fetch data when path or pageUpdate changes
  useEffect(() => {
    fetchFiles(path);
    fetchFoldersAndFiles(path);
  }, [path, pageUpdate]);

  return (
    <div className="pt-6">
      {/* Breadcrumbs */}
      <Breadcrumb path={path} onClick={handleBreadcrumbClick} />

      <div className="flex justify-between items-center mb-4 overflow-auto">
        <h3 className="text-lg font-semibold">Folders</h3>
        <div className="flex">
          <button
            onClick={() => setIsFolderModalOpen(true)}
            className="mr-6 flex items-center text-blue-500 hover:text-blue-700"
          >
            <AiOutlineFolderAdd size={20} className="mr-1" /> Add Folder
          </button>
          <button
            onClick={() => setIsAddFileModalOpen(true)}
            className="flex items-center text-blue-500 hover:text-blue-700"
          >
            <AiOutlineFileAdd size={20} className="mr-1" /> Add File
          </button>
        </div>
      </div>

      {loading ? (
        <p><Loader /></p>
      ) : (
        <>
          {existingFolders || allFiles ? (
            <div className="space-y-4">
              {existingFolders.map((folder, index) => (
                <div key={index} className="bg-gray-100 p-4 rounded-md shadow-sm">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleFolderToggle(index, folder.name)}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <span className="ml-2 font-medium">{folder.name}</span>
                    </button>
                    <div>
                      <button
                        onClick={() => handleEdit(folder, 'folder')}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <AiOutlineEdit size={20} />
                      </button>
                      <button
                        onClick={() => openDeleteConfirmation(folder, 'folder')}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <AiOutlineDelete size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Render files */}
              {allFiles ? (
                allFiles.map((file, index) => (
                  <div key={index} >
                    <div key={index} className="bg-gray-100 p-4 rounded-md shadow-sm">
                      <div className='flex items-center justify-between'>
                        <button
                          onClick={() => handleFileClick(file)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <span className="ml-2 font-medium">{file.fileName}</span>

                        </button>

                        <div>
                          <button
                            onClick={() => handleEdit(file, 'file')}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <AiOutlineEdit size={20} />
                          </button>
                          <button
                            onClick={() => openDeleteConfirmation(file, 'file')}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            <AiOutlineDelete size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">No files available</p>
              )}
            </div>
          ) : (
            <p className="text-gray-600">No folders or files available</p>
          )}

          {open && selectedFile && (
            <FileRendererWithDialog
              file={selectedFile}
              open={open}
              onClose={closeModal}
            />
          )}

        </>
      )}

      {/* Modals */}
      {isFolderModalOpen && (
        <AddFolderModal
          isOpen={isFolderModalOpen}
          folderDetails={itemToEdit} // Pass folder data for editing
          editing={!!itemToEdit} // Indicate edit mode if itemToEdit is not null
          onClose={() => {
            setIsFolderModalOpen(false);
            setItemtoEdit(null); // Clear the edit item
          }}
          main={path}
          project_id={project_id}
          update={update}
        />

      )}
      {isAddFileModalOpen && (
        <AddFileModal
          isOpen={isAddFileModalOpen}
          fileDetails={itemToEdit} // Pass file data for editing
          editing={!!itemToEdit} // Indicate edit mode if itemToEdit is not null
          onClose={() => {
            setIsAddFileModalOpen(false);
            setItemtoEdit(null); // Clear the edit item
          }}
          main={path}
          project_id={project_id}
          update={update}
        />

      )}
      <ConfrimDelete
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this ${itemToDelete?.type === 'file' ? 'file' : 'folder'
          }?`}
      />

    </div>
  );
};

export default FolderFileManager;
